/**
 * Normalização de telefone brasileiro e o problema do nono dígito.
 *
 * Ver docs/ARCHITECTURE.md §9.6. Resumo do porquê isto existe:
 *
 * Números móveis brasileiros ganharam um nono dígito (sempre `9`) entre 2012 e 2016.
 * O `wa_id` que a Meta devolve para o Brasil **frequentemente vem sem ele** — no formato
 * `55DDXXXXXXXX`, com 12 dígitos. Já a agenda importada do celular do médico e o que a
 * secretária digita costumam vir com o 9.
 *
 * Sem uma regra canônica, o mesmo paciente vira dois contatos, o lembrete vai para a
 * conversa errada, e o índice anti-duplicata de campanha não protege nada.
 *
 * Regra deste módulo: a forma **canônica é a de 9 dígitos** (a atual). A forma legada é
 * preservada em `alt` para casamento. Quando o `wa_id` é conhecido, ele vence — o telefone
 * é atributo do contato, a identidade é o `wa_id`.
 */
/**
 * DDDs válidos no Brasil. Lista fechada de propósito: um DDD inexistente é quase sempre
 * erro de digitação ou número estrangeiro mal formatado, e falhar aqui é mais barato do
 * que descobrir na resposta da Meta com `131026`.
 */
const DDD_VALIDOS = new Set([
    // SP
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    // RJ / ES
    '21',
    '22',
    '24',
    '27',
    '28',
    // MG
    '31',
    '32',
    '33',
    '34',
    '35',
    '37',
    '38',
    // PR / SC
    '41',
    '42',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '49',
    // RS
    '51',
    '53',
    '54',
    '55',
    // DF / GO / TO / MT / MS / AC / RO
    '61',
    '62',
    '63',
    '64',
    '65',
    '66',
    '67',
    '68',
    '69',
    // BA / SE
    '71',
    '73',
    '74',
    '75',
    '77',
    '79',
    // PE / AL / PB / RN / CE / PI
    '81',
    '82',
    '83',
    '84',
    '85',
    '86',
    '87',
    '88',
    '89',
    // PA / AM / RR / AP / MA
    '91',
    '92',
    '93',
    '94',
    '95',
    '96',
    '97',
    '98',
    '99',
]);
/** Apenas os dígitos. Aceita `+`, espaço, parênteses, hífen e ponto. */
export function onlyDigits(input) {
    return input.replace(/\D+/g, '');
}
function brand(value) {
    return value;
}
/**
 * Analisa um telefone brasileiro em qualquer forma usual e devolve as duas variantes.
 *
 * Aceita, entre outras: `+55 11 98765-4321`, `5511987654321`, `11987654321`,
 * `(11) 8765-4321`, `1187654321`, `011987654321`.
 */
export function parseBR(input) {
    if (!input || input.trim() === '')
        return { ok: false, error: 'EMPTY' };
    let d = onlyDigits(input);
    if (d.length === 0)
        return { ok: false, error: 'NOT_NUMERIC' };
    // Prefixo internacional discado (00) e o zero do DDD antigo.
    if (d.startsWith('00'))
        d = d.slice(2);
    if (d.startsWith('55') && (d.length === 12 || d.length === 13))
        d = d.slice(2);
    else if (d.length === 11 && d.startsWith('0'))
        d = d.slice(1);
    else if (d.length === 12 && d.startsWith('0'))
        d = d.slice(1);
    if (d.length !== 10 && d.length !== 11)
        return { ok: false, error: 'BAD_LENGTH' };
    const ddd = d.slice(0, 2);
    if (!DDD_VALIDOS.has(ddd))
        return { ok: false, error: 'UNKNOWN_DDD' };
    const sub = d.slice(2);
    const first = sub[0];
    if (first === undefined)
        return { ok: false, error: 'BAD_SUBSCRIBER' };
    if (sub.length === 9) {
        // Forma atual de móvel. O nono dígito é sempre 9.
        if (first !== '9')
            return { ok: false, error: 'BAD_SUBSCRIBER' };
        return {
            ok: true,
            value: {
                e164: brand(`+55${ddd}${sub}`),
                alt: brand(`+55${ddd}${sub.slice(1)}`),
                ddd,
                kind: 'mobile',
                country: 'BR',
            },
        };
    }
    // sub.length === 8: móvel legado (6–9) ou fixo (2–5).
    if (first >= '6' && first <= '9') {
        return {
            ok: true,
            value: {
                e164: brand(`+55${ddd}9${sub}`), // canônica é a de 9 dígitos
                alt: brand(`+55${ddd}${sub}`),
                ddd,
                kind: 'mobile',
                country: 'BR',
            },
        };
    }
    if (first >= '2' && first <= '5') {
        return {
            ok: true,
            value: { e164: brand(`+55${ddd}${sub}`), alt: null, ddd, kind: 'landline', country: 'BR' },
        };
    }
    return { ok: false, error: 'BAD_SUBSCRIBER' };
}
/**
 * Converte o `wa_id` da Meta (dígitos, sem `+`) para as variantes brasileiras.
 *
 * ⚠️ O `wa_id` **não é o telefone**: a doc da Meta adverte que *"a WhatsApp user's phone
 * number and ID may not always match"*. Use isto para casar contato, nunca como identidade.
 * A identidade da conversa é o `wa_id` cru.
 */
export function waIdToBR(waId) {
    const d = onlyDigits(waId);
    if (!d.startsWith('55'))
        return { ok: false, error: 'NOT_BR' };
    return parseBR(d);
}
/**
 * O telefone canônico de um `wa_id`, que é a chave de casamento de contato (ADR 0028).
 *
 * Brasileiro sai na forma de 9 dígitos, com a legada em `alt`; estrangeiro sai como
 * `+dígitos`, sem variante. BSUID não é telefone e devolve `null`.
 */
export function canonicoDoWaId(waId) {
    if (!/^\+?\d+$/.test(waId))
        return null;
    const br = waIdToBR(waId);
    if (br.ok)
        return { e164: br.value.e164, alt: br.value.alt };
    return { e164: brand(`+${onlyDigits(waId)}`), alt: null };
}
/**
 * Todas as formas pelas quais o mesmo assinante pode aparecer. Use em `WHERE ... IN`
 * ao procurar um contato por telefone.
 */
export function variants(p) {
    return p.alt ? [p.e164, p.alt] : [p.e164];
}
/** Duas entradas quaisquer se referem ao mesmo assinante? */
export function sameSubscriber(a, b) {
    const pa = parseBR(a);
    const pb = parseBR(b);
    if (!pa.ok || !pb.ok)
        return false;
    return pa.value.e164 === pb.value.e164;
}
/** `+5511987654321` → `(11) 98765-4321`. Só para exibição. */
export function formatBR(p) {
    const sub = p.e164.slice(5);
    return p.kind === 'mobile'
        ? `(${p.ddd}) ${sub.slice(0, 5)}-${sub.slice(5)}`
        : `(${p.ddd}) ${sub.slice(0, 4)}-${sub.slice(4)}`;
}
//# sourceMappingURL=phone.js.map